import React, { useState, Fragment, useRef } from 'react'
import ReactDOM from 'react-dom'
import Tippy from 'blocks.simple/tippy/tippy'
import { cn } from 'ethcss'
import styles from './dotTooltip.jcss'
import type { DotTooltipChildProps, DotTooltipDefaultProps, DotTooltipProps } from './dotTooltip.types'

const DotTooltip = (p_: DotTooltipProps) => {
    const [originalWidth, setOriginalWidth] = useState(0)
    const [contentWidth, setContentWidth] = useState(0)
    const originalWrapperRef = useRef<HTMLElement | null>(null)
    const hiddenWrapperRef = useRef<HTMLDivElement | null>(null)

    function getChildrenWithRef(children: React.ReactNode, ref: React.Ref<HTMLElement>) {
        return React.Children.map(children, (child) => {
            if (!React.isValidElement<DotTooltipChildProps>(child)) {
                return child
            }

            return React.cloneElement(child, {
                ref,
                onMouseEnter,
            })
        })
    }

    function onMouseEnter() {
        const originalElement = originalWrapperRef.current
        const hiddenElement = hiddenWrapperRef.current

        if (!originalElement || !hiddenElement) {
            return
        }

        const originalRect = originalElement.getClientRects()[0]
        const hiddenRect = hiddenElement.getClientRects()[0]

        if (!originalRect || !hiddenRect) {
            return
        }

        setOriginalWidth(hiddenRect.width)
        setContentWidth(originalRect.width)
    }

    const isShowTooltip = contentWidth < originalWidth && !p_.tooltip.disabled

    let content: React.ReactNode[] = [
        <div className={cn(p_.containerClass)} key={`originalContent`}>
            {p_.children ? getChildrenWithRef(p_.children, originalWrapperRef) : null}
        </div>,
    ]

    if (isShowTooltip) {
        content = [
            <Tippy
                key={`originalDotTooltipWrapper_${p_.id}`}
                className={cn(p_.containerClass)}
                style={{ display: 'block' }}
                {...p_.tooltip}
            >
                {p_.children ? getChildrenWithRef(p_.children, originalWrapperRef) : null}
            </Tippy>,
        ]
    }

    const hiddenOriginalTextContent = (
        <div key={`hiddenDotTooltipWrapper_${p_.id}`} ref={hiddenWrapperRef} className={styles.hidden}>
            {p_.children}
        </div>
    )

    const element = p_.portalId ? document.getElementById(p_.portalId) : null

    if (element) {
        content.push(ReactDOM.createPortal(hiddenOriginalTextContent, element))
    } else {
        content.push(hiddenOriginalTextContent)
    }

    return <Fragment>{content}</Fragment>
}

const defaultProps: DotTooltipDefaultProps = {
    tooltip: {
        disabled: true,
    },
    portalId: null,
}

DotTooltip.defaultProps = defaultProps

export default DotTooltip
